import { Alert, FormControlLabel, Switch } from '@mui/material';
import React from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { Snackbar } from 'src/components/snackbar';
import { SelectDropdownWidget } from 'src/components/widget';
import { useGetAllLayoutType } from 'src/hooks';
import {
  generateSingleConfigString,
  getGridOptions,
} from 'src/hooks/api/layouts/helpers';
import { useDeleteRotationScreenSection } from 'src/hooks/api/layouts/useDeleteRotationScreenSection';
import { useUpdateLayout } from 'src/hooks/api/layouts/useUpdateLayout';
import { useGlobalComponentsScreen } from 'src/hooks/useGlobalComponentsScreen';
import {
  IDisclaimerPosition,
  IDisclaimerWidth,
  IScreenOrientation,
  LayoutTypes,
  SectionTypes,
} from 'src/interfaces';
import {
  isCurrentScreenWithCOD,
  isCurrentScreenWithCODThankYou,
  isCurrentScreenWithDYR,
  lang,
} from 'src/utils';

import { SimpleDialog } from '../simpleDialog';
import { ScreenConfigProps } from './ScreenConfigModal.interface';

export const ScreenConfigModal = ({
  screenConfigModal,
  layout,
  configScreenIndex,
  screenName,
  layoutId,
  formMethods,
  isRotationScreen,
  setRotationGridSections,
}: ScreenConfigProps) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const updateTempLayout = useUpdateLayout(
    isRotationScreen ? 'rotation' : 'config',
  );
  const deleteRotationScreenSection = useDeleteRotationScreenSection();
  const { data: layoutTypes = [] } = useGetAllLayoutType();
  const globalComponentsScreen = useGlobalComponentsScreen(layout);
  const isDisclaimerChecked = formMethods.watch('disclaimer');

  const screenConfigString = isRotationScreen
    ? layout?.config?.rotationGridLines?.[configScreenIndex]
    : layout?.config?.gridLines?.[configScreenIndex];
  const isCurrentConfigDisclaimer: boolean =
    screenConfigString?.includes('Disclaimer') ?? false;
  const otherConfigsContainDisclaimer = layout?.config?.gridLines?.some(
    (item, index) => index !== configScreenIndex && item.includes('Disclaimer'),
  );
  const isLastDisclaimerBeingRemoved =
    isCurrentConfigDisclaimer &&
    !otherConfigsContainDisclaimer &&
    !isDisclaimerChecked;
  const isDigitalMerch =
    layoutTypes.find((type) => type.id === Number(layout?.layout_type_id))
      ?.name === LayoutTypes.DIGITAL_MERCHANDISER;

  const saveScreenConfig = async (formData: FieldValues) => {
    const payload = {
      ...formData,
      id: layoutId,
      index: configScreenIndex,
      layout,
      isRotationScreen,
    };
    if (generateSingleConfigString(payload) === screenConfigString) {
      screenConfigModal.onCloseModal();
      formMethods.reset(undefined, { keepValues: true });

      return;
    }

    setIsLoading(true);

    if (isRotationScreen) {
      if (
        layout?.screens?.[configScreenIndex].sections.some(
          (section) => section.config?.events?.SECTION_TIMER !== undefined,
        )
      ) {
        await deleteRotationScreenSection.mutateAsync({
          screen: layout?.screens?.[configScreenIndex],
        });
      }
      setRotationGridSections?.([]);
    }

    await updateTempLayout.mutateAsync(payload);
    Snackbar.success(lang.editLayoutsSidebar.successMessage(configScreenIndex));

    formMethods.reset(undefined, { keepValues: true });

    setIsLoading(false);
    screenConfigModal.onCloseModal();
  };

  const validateField = (name: string) => {
    if (isDisclaimerChecked && !formMethods.getValues(name)) {
      return 'This field is required.';
    }

    return undefined;
  };

  const shouldDisableOptions = () => {
    if (isRotationScreen) return false;
    if (isCurrentScreenWithCOD(configScreenIndex + 1, globalComponentsScreen)) {
      return layout.config?.sectionTypes?.includes(SectionTypes.COD);
    }
    if (
      isCurrentScreenWithCODThankYou(
        configScreenIndex + 1,
        globalComponentsScreen,
      ) ||
      isCurrentScreenWithDYR(configScreenIndex + 1, globalComponentsScreen)
    ) {
      return layout.config?.sectionTypes?.includes(SectionTypes.DYR);
    }

    return false;
  };

  const onScreenConfigClose = () => {
    formMethods.reset(undefined, { keepValues: true });
    screenConfigModal.onCloseModal();
  };

  return (
    <SimpleDialog
      closeOnBackdropClick={false}
      open={screenConfigModal.open}
      isLoading={isLoading}
      title={lang.editLayoutsSidebar.configureScreen(screenName)}
      onClose={onScreenConfigClose}
      onClickCancel={onScreenConfigClose}
      disabled={
        isLastDisclaimerBeingRemoved && !isDigitalMerch && !isRotationScreen
      }
      onClickConfirm={formMethods.handleSubmit(saveScreenConfig)}>
      <Controller
        name="orientation"
        control={formMethods.control}
        defaultValue=""
        render={({ field }) => (
          <SelectDropdownWidget
            {...field}
            selectOptions={[
              { label: IScreenOrientation.HORIZONTAL, value: 'horizontal' },
              { label: IScreenOrientation.VERTICAL, value: 'vertical' },
            ]}
            label={lang.assemblyLayouts.screenSetup.orientation}
            value={field?.value}
            disabled
          />
        )}
      />

      <Controller
        name="screen_grid"
        control={formMethods.control}
        rules={{ required: 'This field is required.' }}
        defaultValue=""
        render={({ field, fieldState }) => (
          <SelectDropdownWidget
            {...field}
            label={lang.assemblyLayouts.screenSetup.gridLabel}
            value={field?.value}
            onChange={(e) => field?.onChange(e)}
            selectOptions={getGridOptions(layout?.screens[configScreenIndex])}
            hasError={!!fieldState?.error}
            error={fieldState?.error?.message}
            disabled={shouldDisableOptions()}
          />
        )}
      />
      {isRotationScreen ? (
        <Alert
          severity={'warning'}
          icon={false}
          sx={{ marginBottom: 2, width: '100%' }}>
          {lang.editLayoutsSidebar.warnRotationScreenConfigSave}
        </Alert>
      ) : (
        <>
          <Controller
            name="disclaimer"
            control={formMethods.control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={field?.value}
                    onChange={(e) => {
                      field?.onChange(e.target.checked);
                      formMethods.setValue('placement', '');
                      formMethods.setValue('size', '');
                    }}
                    disabled={shouldDisableOptions()}
                  />
                }
                label={lang.assemblyLayouts.screenSetup.disclaimerLabel}
              />
            )}
          />

          <Controller
            name="placement"
            control={formMethods.control}
            rules={{ validate: () => validateField('placement') }}
            defaultValue=""
            render={({ field, fieldState }) => (
              <SelectDropdownWidget
                {...field}
                label={lang.assemblyLayouts.screenSetup.placementLabel}
                disabled={!isDisclaimerChecked}
                value={field?.value}
                data-testid="Placement"
                onChange={(e) => field?.onChange(e)}
                selectOptions={[
                  {
                    label: IDisclaimerPosition.TOP,
                    value: IDisclaimerPosition.TOP,
                  },
                  {
                    label: IDisclaimerPosition.BOTTOM,
                    value: IDisclaimerPosition.BOTTOM,
                  },
                ]}
                hasError={!!fieldState?.error}
                error={fieldState?.error?.message}
              />
            )}
          />

          <Controller
            name="size"
            control={formMethods.control}
            rules={{ validate: () => validateField('size') }}
            defaultValue=""
            render={({ field, fieldState }) => (
              <SelectDropdownWidget
                {...field}
                label={lang.assemblyLayouts.screenSetup.sizeLabel}
                disabled={!isDisclaimerChecked}
                value={field?.value}
                onChange={(e) => field?.onChange(e)}
                selectOptions={[
                  {
                    label: IDisclaimerWidth.HALF,
                    value: IDisclaimerWidth.HALF,
                  },
                  {
                    label: IDisclaimerWidth.FULL,
                    value: IDisclaimerWidth.FULL,
                  },
                ]}
                hasError={!!fieldState?.error}
                error={fieldState?.error?.message}
              />
            )}
          />

          <Alert
            severity={
              isLastDisclaimerBeingRemoved && !isDigitalMerch
                ? 'error'
                : 'warning'
            }
            icon={false}
            sx={{ marginBottom: 2, width: '100%' }}>
            {isLastDisclaimerBeingRemoved && !isDigitalMerch
              ? lang.editLayoutsSidebar.errorLastDisclaimer
              : lang.editLayoutsSidebar.warnScreenConfigSave}
          </Alert>
        </>
      )}
    </SimpleDialog>
  );
};
